import api, { PermissionType, UnderwritingCategoryConfig, UnderwritingStepConfig } from '@api';
import {
    ArrowDownward, ArrowUpward, Edit, RemoveCircleOutline
} from '@mui/icons-material';
import { Paper, Typography } from '@mui/material';
import {
    IconButton, SubtleLink, useConfirm, usePageMessage
} from '@tsp-ui/core';
import {
    findAdjacentActiveDisplayOrder, getMaxActiveDisplayOrder, getMinActiveDisplayOrder,
    useGetCurrentAccount, useHasPermission
} from '@utils';
import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';

import styles from './UnderwritingCategoryConfigCard.module.scss';
import { UnderwritingStepContext } from './UnderwritingCategoryDetailPage';


interface UnderwritingStepConfigCardProps {
    underwritingStepConfig: UnderwritingStepConfig;
    categoryConfig: UnderwritingCategoryConfig;
    onUpdate: () => Promise<void>;
}

export function UnderwritingStepConfigCard({
    underwritingStepConfig, categoryConfig, onUpdate
}: UnderwritingStepConfigCardProps) {
    const { stepConfigs } = useContext(UnderwritingStepContext);
    const [ canManage ] = useHasPermission([ PermissionType.MANAGE_UNDERWRITING_CATEGORIES ]);
    const pageMessage = usePageMessage();
    const confirm = useConfirm();
    const { id: clientId } = useGetCurrentAccount();

    const [ moveUpLoading, setMoveUpLoading ] = useState(false);
    const [ moveDownLoading, setMoveDownLoading ] = useState(false);
    const [ isDeleting, setIsDeleting ] = useState(false);

    const activeStepConfigs = stepConfigs.filter(config => config.isActive !== false);

    async function moveConfig(direction: 'up' | 'down') {
        const setLoading = direction === 'up' ? setMoveUpLoading : setMoveDownLoading;
        setLoading(true);

        try {
            const targetDisplayOrder = findAdjacentActiveDisplayOrder(
                stepConfigs,
                underwritingStepConfig.displayOrder,
                direction
            );

            if (targetDisplayOrder === null) {
                throw new Error(`Cannot move ${direction}, no active config found in that direction`);
            }

            // Update the current config's display order
            await api.underwriting.updateUnderwritingStepConfig(clientId, categoryConfig.id, {
                ...underwritingStepConfig,
                displayOrder: targetDisplayOrder
            });

            // Find the config that we're swapping with
            const swapConfig = activeStepConfigs.find(c => c.displayOrder === targetDisplayOrder);

            // Update the display order of the config we're swapping with
            if (swapConfig) {
                await api.underwriting.updateUnderwritingStepConfig(clientId, categoryConfig.id, {
                    ...swapConfig,
                    displayOrder: underwritingStepConfig.displayOrder
                });
            }

            await onUpdate();
        } catch (error) {
            pageMessage.handleApiError('An error occurred while moving the underwriting step configuration', error);
        }

        setLoading(false);
    }

    async function onDelete() {
        if (!await confirm('Are you sure you want to delete this underwriting step configuration? All associated questions will be deleted.')) {
            return;
        }

        setIsDeleting(true);

        try {
            await api.underwriting.deleteUnderwritingStepConfig(clientId, categoryConfig.id, underwritingStepConfig);
            await onUpdate();
            pageMessage.success('Underwriting step configuration deleted');
        } catch (error) {
            pageMessage.handleApiError('An error occurred while deleting the underwriting step configuration', error);
        }

        setIsDeleting(false);
    }

    // Get min and max display orders of active configs only
    const minActiveDisplayOrder = getMinActiveDisplayOrder(stepConfigs);
    const maxActiveDisplayOrder = getMaxActiveDisplayOrder(stepConfigs);

    return (
        <Paper
            className={styles.root}
            elevation={0}
        >
            <div>
                <SubtleLink
                    to={`step/${underwritingStepConfig.id}`}
                >
                    {underwritingStepConfig.name}
                </SubtleLink>

                <Typography
                    variant="body2"
                    color="textSecondary"
                >
                    {/* eslint-disable-next-line max-len */}
                    {underwritingStepConfig.questionCount > 0
                        ? `${underwritingStepConfig.questionCount} question${underwritingStepConfig.questionCount === 1 ? '' : 's'}`
                        : 'No questions have been added to this configuration'}
                </Typography>
            </div>

            <span className={styles.icons}>
                <IconButton
                    loading={moveUpLoading}
                    tooltip={!canManage ? 'You don\'t have permission to move configs' : 'Move up'}
                    disabled={underwritingStepConfig.displayOrder === minActiveDisplayOrder
                        || underwritingStepConfig.displayOrder === undefined
                        || !canManage}
                    onClick={() => moveConfig('up')}
                >
                    <ArrowUpward color="secondary" />
                </IconButton>

                <IconButton
                    loading={moveDownLoading}
                    tooltip={!canManage
                        ? 'You don\'t have permission to move configs' : 'Move down'}
                    disabled={
                        underwritingStepConfig.displayOrder === undefined
                            || underwritingStepConfig.displayOrder === maxActiveDisplayOrder
                            || !canManage
                    }
                    onClick={() => moveConfig('down')}
                >
                    <ArrowDownward color="secondary" />
                </IconButton>

                <IconButton
                    tooltip={canManage ? 'Edit underwriting category configuration' : 'You do not have permission to edit underwriting category configurations'}
                    disabled={!canManage}
                    component={Link}
                    to={`${underwritingStepConfig.id}`}
                >
                    <Edit color="secondary" />
                </IconButton>

                <IconButton
                    tooltip={canManage ? 'Delete underwriting step configuration' : 'You do not have permission to delete underwriting step configurations'}
                    disabled={!canManage}
                    onClick={onDelete}
                    loading={isDeleting}
                    autoMarginLoader={false}
                >
                    <RemoveCircleOutline color="error" />
                </IconButton>
            </span>
        </Paper>
    );
}
